import {
  effect,
  fromPromise,
  LiveData,
  onComplete,
  OnEvent,
  onStart,
  Service,
  smartRetry,
} from '@toeverything/infra';
import { catchError, EMPTY, switchMap, tap } from 'rxjs';

import { AccountChanged } from '../events/account-changed';
import type {
  LlmModel,
  LlmProvider,
  LlmProviderStore,
  UpsertLlmModelInput,
  UpsertLlmProviderInput,
} from '../stores/llm-provider';

@OnEvent(AccountChanged, e => e.onAccountChanged)
export class LlmProviderService extends Service {
  constructor(private readonly store: LlmProviderStore) {
    super();
  }

  providers$ = new LiveData<LlmProvider[] | null>(null);
  isRevalidating$ = new LiveData(false);
  error$ = new LiveData<any>(null);

  revalidate = effect(
    switchMap((workspaceId: string) => {
      return fromPromise(signal => {
        return this.store.listProviders(workspaceId, signal);
      }).pipe(
        smartRetry(),
        tap(providers => {
          this.providers$.value = providers;
        }),
        catchError(error => {
          this.error$.value = error;
          return EMPTY;
        }),
        onStart(() => {
          this.isRevalidating$.value = true;
        }),
        onComplete(() => {
          this.isRevalidating$.value = false;
        })
      );
    })
  );

  async waitForRevalidation(workspaceId: string, signal?: AbortSignal) {
    this.revalidate(workspaceId);
    await this.isRevalidating$.waitFor(
      isRevalidating => !isRevalidating,
      signal
    );
  }

  async upsertProvider(
    workspaceId: string,
    input: UpsertLlmProviderInput
  ): Promise<LlmProvider> {
    const provider = await this.store.upsertProvider(workspaceId, input);
    await this.waitForRevalidation(workspaceId);
    return provider;
  }

  async deleteProvider(workspaceId: string, id: string): Promise<boolean> {
    const ok = await this.store.deleteProvider(workspaceId, id);
    await this.waitForRevalidation(workspaceId);
    return ok;
  }

  async upsertModel(
    workspaceId: string,
    input: UpsertLlmModelInput
  ): Promise<LlmModel> {
    const model = await this.store.upsertModel(workspaceId, input);
    await this.waitForRevalidation(workspaceId);
    return model;
  }

  async setModelEnabled(
    workspaceId: string,
    id: string,
    enabled: boolean
  ): Promise<boolean> {
    const ok = await this.store.setModelEnabled(workspaceId, id, enabled);
    await this.waitForRevalidation(workspaceId);
    return ok;
  }

  async setDefaultModel(
    workspaceId: string,
    modelId: string
  ): Promise<boolean> {
    const ok = await this.store.setDefaultModel(workspaceId, modelId);
    await this.waitForRevalidation(workspaceId);
    return ok;
  }

  async deleteModel(workspaceId: string, id: string): Promise<boolean> {
    const ok = await this.store.deleteModel(workspaceId, id);
    await this.waitForRevalidation(workspaceId);
    return ok;
  }

  private onAccountChanged() {
    this.providers$.value = null;
  }
}
